import * as React from "react";
import {
  GridColDef,
  GridRenderCellParams,
  GridRowsProp,
  GridSortModel,
  GridRowModel,
  GridToolbarContainer,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Link,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { GlassDataGrid, StyledTextField, CardBase } from "../../components/global";
import ConfirmDeleteModal from "../../components/ConfirmDeleteModal";
import NoResultsOverlay from "../../components/NoResultOverlay";
import { toast } from "react-toastify";
import useDebounce from "../../hooks/useDebounce";
import { useTitle } from "../../hooks/useTitle";
import { useUILabel } from "../../hooks/useUILabel";
import { Template } from "../../types/template";
import { useNavigate } from "react-router-dom";
import {
  useDeleteTemplateMutation,
  useGetTemplatesQuery,
} from "../../services/template.service";
import { useCompany } from "../../hooks/useCompany";
import { OpenInNew, Person, Place } from "@mui/icons-material";
import ProjectTemplateDrawer from "./ProjectTemplateDrawer";
import dayjs from "dayjs";

const TableToolbar = ({
  onSearch,
  onCreate,
}: {
  onSearch: (query?: string) => void;
  onCreate: () => void;
}) => {
  const enterToSearchLabel = useUILabel("common.enterToSearch");
  const resetLabel = useUILabel("common.reset");
  const createNewTemplateLabel = useUILabel("project.createNewTemplate");
  const [query, setQuery] = React.useState<string | undefined>(undefined);
  const debounceQuery = useDebounce(query, 500);

  React.useEffect(() => {
    onSearch(debounceQuery);
  }, [debounceQuery]);

  return (
    <GridToolbarContainer
      style={{ paddingLeft: 10, paddingRight: 10, marginBottom: 5 }}
    >
      <StyledTextField
        margin="dense"
        label={enterToSearchLabel}
        value={query || ""}
        onChange={(e) => setQuery(e.target.value)}
        sx={{ minWidth: 250 }}
      />
      {!!query && (
        <Button variant="text" sx={{ mt: 1 }} onClick={() => setQuery("")}>
          {resetLabel}
        </Button>
      )}
      <Stack sx={{ flex: 1 }} direction="row" justifyContent="flex-end" alignItems="center">
        <Button
          variant="contained"
          color="success"
          onClick={onCreate}
          startIcon={<AddIcon />}
          sx={{
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          {createNewTemplateLabel}
        </Button>
      </Stack>
    </GridToolbarContainer>
  );
};

const ProjectTemplatesPage = () => {
  const templatesLabel = useUILabel("project.templates");
  const workIdWorkTitleLabel = useUILabel("project.workIdWorkTitle");
  const paymentTypeLabel = useUILabel("project.paymentType");
  const lastUpdateLabel = useUILabel("project.lastUpdate");
  const viewEditDeleteLabel = useUILabel("project.viewEditDelete");
  const createNewTemplateLabel = useUILabel("project.createNewTemplate");

  useTitle(templatesLabel);
  const navigate = useNavigate();
  const company = useCompany();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDark = theme.palette.mode === "dark";

  const [deleteModal, setDeleteModal] = React.useState<{
    open: boolean;
    row: GridRowModel<Template> | null;
  }>({
    open: false,
    row: null,
  });

  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(100);
  const [sortModel, setSortModel] = React.useState<GridSortModel>([]);
  const [searchQuery, setSearchQuery] = React.useState<string | undefined>(
    undefined
  );
  const [open, setOpen] = React.useState(false);
  const [currentItem, setCurrentItem] = React.useState<Template | null>(null);

  const { data, isLoading } = useGetTemplatesQuery({
    page,
    pageSize,
    sortField: sortModel.length > 0 ? sortModel[0].field : undefined,
    sortMode: sortModel.length > 0 ? sortModel[0].sort : undefined,
    search: searchQuery,
  });
  const rowCount = React.useMemo(() => data?.meta.total, [data]);

  const [doDelete] = useDeleteTemplateMutation();

  const rows: GridRowsProp | undefined = React.useMemo(
    () => data?.data,
    [data]
  );

  const handleDeleteRow = React.useCallback((row: GridRowModel<Template>) => {
    setDeleteModal({ open: true, row });
  }, []);

  const handleEditRow = React.useCallback(
    (row: GridRowModel<Template>) => {
      navigate(`/works/templates/${row.id}/edit`);
    },
    [navigate]
  );

  const columns: GridColDef[] = React.useMemo(
    () => [
      {
        field: "title",
        headerName: workIdWorkTitleLabel,
        flex: 1,
        minWidth: isMobile ? 220 : 300,
        sortingOrder: ["desc", "asc", null],
        filterable: true,
        renderCell: (params: GridRenderCellParams) => (
          <Box>
            <Link
              component="button"
              variant="body2"
              onClick={() => {
                setCurrentItem(params.row);
                setOpen(true);
              }}
            >
              #{params.row.id} - {params.row.title}
            </Link>
            {!!params.row.customer ? (
              <Typography variant="body2">
                <Person fontSize="inherit" color="disabled" />{" "}
                {params.row.customer?.first_name}
              </Typography>
            ) : !!params.row.location ? (
              <Typography variant="body2">
                <Place fontSize="inherit" color="disabled" />{" "}
                {params.row.location.name}
              </Typography>
            ) : (
              <Typography>?</Typography>
            )}
          </Box>
        ),
      },
      {
        field: "type",
        headerName: paymentTypeLabel,
        headerAlign: "center",
        minWidth: isMobile ? 110 : 150,
        sortable: true,
        filterable: false,
        align: "center",
        hide: isMobile,
        valueGetter: (params: GridValueGetterParams) => {
          if (!params.row) return "N/A";
          return params.row.type == "hourly" ? `Hourly Rate` : `Fixed Rate`;
        },
      },
      {
        field: "updated_at",
        headerName: lastUpdateLabel,
        headerAlign: "center",
        minWidth: isMobile ? 110 : 150,
        sortable: true,
        filterable: false,
        align: "center",
        hide: isMobile,
        valueGetter: (params: GridValueGetterParams) => {
          if (!params.row || !params.row.created_at) return "N/A";
          return dayjs(params.row.created_at).format("MMM DD, YYYY");
        },
      },
      {
        field: "control",
        headerName: viewEditDeleteLabel,
        headerAlign: "center",
        minWidth: isMobile ? 120 : 150,
        sortable: false,
        filterable: false,
        align: "center",
        renderCell: (params: GridRenderCellParams) => (
          <Stack direction={"row"}>
            <IconButton
              onClick={() => navigate(`/works/templates/${params.row.id}`)}
            >
              <OpenInNew fontSize="small" />
            </IconButton>
            <IconButton onClick={() => handleEditRow(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton onClick={() => handleDeleteRow(params.row)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        ),
      },
    ],
    [handleDeleteRow, handleEditRow, isMobile, navigate, workIdWorkTitleLabel, paymentTypeLabel, lastUpdateLabel, viewEditDeleteLabel]
  );

  const handleSearch = React.useCallback((query?: string) => {
    setSearchQuery(query);
  }, []);

  const handleCreate = React.useCallback(() => {
    navigate(`/works/templates/create`);
  }, []);

  const closeDeleteModal = () =>
    setDeleteModal({ ...deleteModal, open: false });

  const handleDelete = async () => {
    if (deleteModal.row?.id) {
      try {
        await doDelete(deleteModal.row.id).unwrap();
        closeDeleteModal();
        toast.success("Successfully deleted");
      } catch (err) {
        closeDeleteModal();
      }
    }
  };

  const CustomToolbar = React.useMemo(
    () => () =>
      <TableToolbar onSearch={handleSearch} onCreate={handleCreate} />,
    [handleSearch, handleCreate]
  );

  return (
    <Box
      sx={{
        width: "auto",
        px: { xs: 0, sm: 3 },
        py: 3,
      }}
    >
      <CardBase
        title={templatesLabel}
        useGlassCard={true}
        nested={false}
        sx={{
          "& .card-base__header": {
            flexDirection: { xs: "row", sm: "row" },
            alignItems: { xs: "center", sm: "center" },
            gap: { xs: 1.5, sm: 2 },
            flexWrap: { xs: "wrap", sm: "nowrap" },
          },
          "& .card-base__header > .MuiBox-root:first-of-type": {
            flex: 1,
          },
          "& .card-base__header > .MuiBox-root:last-of-type": {
            width: "auto",
          },
        }}
        headerActions={
          <Button
            variant="contained"
            color="success"
            onClick={handleCreate}
            startIcon={<AddIcon />}
            sx={{
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {createNewTemplateLabel}
          </Button>
        }
      >
        <GlassDataGrid
          height="85vh"
          rows={rows || []}
          columns={columns}
          paginationMode="server"
          rowCount={rowCount}
          page={page as any}
          onPageChange={(newPage: any) => setPage(newPage)}
          pageSize={pageSize as any}
          rowsPerPageOptions={[1000]}
          onPageSizeChange={(newSize: any) => setPageSize(newSize)}
          sortModel={sortModel}
          sortingMode="server"
          onSortModelChange={(newSortModel: GridSortModel) => setSortModel(newSortModel)}
          loading={isLoading}
          components={{
            LoadingOverlay: LinearProgress,
            NoResultsOverlay: NoResultsOverlay,
            Toolbar: CustomToolbar,
          }}
          disableSelectionOnClick
          disableColumnFilter
          density="compact"
          sx={{
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: isDark
                ? "rgba(255, 255, 255, 0.08)"
                : "rgba(255, 255, 255, 0.5)",
              backdropFilter: "blur(15px) saturate(180%)",
              WebkitBackdropFilter: "blur(15px) saturate(180%)",
              borderBottom: `2px solid ${isDark ? "rgba(255, 255, 255, 0.15)" : "rgba(46, 125, 60, 0.3)"}`,
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 700,
                fontSize: "0.875rem",
                textTransform: "uppercase",
                letterSpacing: "0.8px",
                color: "#12903C",
              },
            },
          }}
        />
      </CardBase>
      <ConfirmDeleteModal
        open={deleteModal.open}
        onClose={closeDeleteModal}
        onDelete={handleDelete}
        message={`Delete '${deleteModal.row?.title}' ?`}
      />
      <ProjectTemplateDrawer
        data={currentItem!}
        open={open}
        setOpen={setOpen}
      />
    </Box>
  );
};
export default ProjectTemplatesPage;
